
import {Request} from './Request';
import {Utils} from '../common/Utils';
import {BaseModel} from "./BaseModel";

/**
 * These kind of BaseModel represents a data entry from the ARGO Rest API. It handles all CRUD (create, read, update, delete)
 * methods and parsing JSON from the backend to a readable object. An ajax request will be called with the
 * {@link Request} class.
 *
 * Important notice:
 * This model class was designed to work with the "dataEntries" collection (/api/dataEntries/) from ARGO.
 * But there are many other collections, like "users", "types", ... The JSON structures differ completely
 * from collection to collection, that is the reason why it could throw exceptions or does not work as expected.
 *
 */
export class Model extends BaseModel {

  /**
   *
   * @param {String} [type] - name of the content type (only exists for dataEntries)
   * @param {Object} options - configuration options
   */
  constructor(type, options = {}) {

    // type can be the first argument or defined in options or taken by list
    if (!Utils.isString(type)) {
      options = type;
      type = options.type || null;

      if (!type && options.listReference) {
        type = options.listReference.type || null;
      }
    }

    // to be compatible
    if (options.dataEntry) {
      options.data = options.dataEntry;
    }

    // for this model only "dataEntries" collection will be supported
    options.collection = 'dataEntries';
    super(options);

    /**
     * Content type name
     * @type {String}
     */
    this.type = type;
    /**
     * Expanded properties and its values as an array of data entries
     * @type {Object}
     * @private
     */
    this._expanded || (this._expanded = {});
    /**
     * Name of the primary key from the content type. Normally it's called 'id'.
     * @type {String}
     */
    this.pk = options.pk || 'id';
    // pass pk name from parent DataList if given
    if (options.listReference && options.listReference.pk) {
      this.pk = options.listReference.pk;
    }
    /**
     *
     * @type {*|null}
     */
    this.expandKeys = options.expandKeys || null;
  }

  /**
   * Return the content type name from the data entry.
   * @return {null|String}
   */
  getType() {
    return this.type;
  }

  /**
   * Return model's properties and additional keys like '_id' (system id)
   * @return {Object}
   */
  getData() {
    let data = super.getData();

    // internal data entry ID
    data._id = this.getId();

    // embed sys properties with dangling "_"
    BaseModel.getSysProperties().forEach((it) => {
      data['_' + it.name] = this.getSystemData(it.name);
    });

    // TODO: _expanded ?
    return data;
  }

  /**
   * Modifies the query options for the dataEntry query controller.
   * @param {QueryOptions} options - options passed to the query controller
   */
  onBeforeFetchByFilter(options) {
    options.type = this.type;

    // set additional expand keys if exists
    if (this.expandKeys) {
      options.expand = this.expandKeys;
    }
  }

  /**
   * Search a entry entry by the primary key with the given pkValue.
   * @param {Number|String} pkValue - value from primary key
   * @return {Promise}
   */
  fetchByPk(pkValue) {
    return this.fetchByFilter({property: this.pk, value: pkValue});
  }

  /**
   * Extract "properties" property from data entry and use it as modifiable part
   * of the response.
   * @param {Object} data - JSON response to be parsed
   * @param {Object} options - options passed to parse function
   */
  onBeforeParse(data, options = {}) {

    // extract dataEntry properties
    if (data.properties) {
      this.set(data.properties, {silent: true});
      delete data.properties;
    }

    // extract expanded data if exists (because were cannot be saved)
    if(data._expanded) {
      this._expanded = data._expanded;
      delete data._expanded;
    }
  }

  /**
   * Returns the data object that will be used to sent to the backend.
   * @return {{properties: ({}|*)}}
   * @private
   */
  _getRequestData(props = this.properties) {
    return {
      type: this.type,
      properties: props
    };
  }

  /**
   * Checks the model has an expanded property with given key and the value
   * is not undefined nor null.
   * @param key
   * @return {boolean}
   */
  hasExpanded(key) {
    return this.has(key) && this._expanded.hasOwnProperty(key);
  }

  /**
   * Returns the expanded value from the property with the given name.
   * @param key
   * @return {*}
   */
  getExpanded(key) {
    if (!this.hasExpanded(key)) {
      return null;
    }
    let list = this._expanded[key]._embedded[this._collection];
    return Array.isArray(this.get(key)) ? list : list[0];
  }

  /**
   * Returns the value from the primary of the data entry.
   * @return {*|null}
   */
  getPk() {
    return this.properties[this.pk] || null;
  }
}